import React, { useState, useEffect, useMemo } from 'react';
import { Search, UsersRound, Shield, Download, FileJson, Plus, RefreshCw, Layers } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faUserCheck, faUserSlash } from '@fortawesome/free-solid-svg-icons';
import SiteBrandedLoader, { BrandSpinnerRing } from '../components/SiteBrandedLoader';
import EmptyState from '../components/EmptyState';
import Modal from '../components/Modal';
import { usersService, coursesService, adminService, paymentsService } from '../services/api';
import { User, Course } from '../types';
import { normalizePhoneNumber, isValidIranianPhone } from '../utils/phoneUtils';

const EditIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const DeleteIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const PeopleIcon = () => (
  <svg className="h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
  </svg>
);

const formLabelCls = 'mb-1.5 block text-xs font-bold text-slate-600';
const formInputCls =
  'w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-900 shadow-[0_1px_2px_rgba(15,23,42,0.04)] outline-none transition focus:border-indigo-400 focus:ring-[3px] focus:ring-indigo-500/18';
const formInputErrorCls =
  'border-rose-300 focus:border-rose-400 focus:ring-rose-500/20';
const modalSectionTitleCls = 'border-b border-slate-100 pb-2 text-sm font-black text-slate-800';

const normalizeDigits = (value: string) =>
  value
    .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
    .replace(/[^\d]/g, '');

const formatThousands = (value: string) => {
  const digits = normalizeDigits(value);
  if (!digits) return '';
  return Number(digits).toLocaleString('en-US');
};

const threeDigitsToWords = (n: number): string => {
  const ones = ['', 'یک', 'دو', 'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه'];
  const teens = ['ده', 'یازده', 'دوازده', 'سیزده', 'چهارده', 'پانزده', 'شانزده', 'هفده', 'هجده', 'نوزده'];
  const tens = ['', '', 'بیست', 'سی', 'چهل', 'پنجاه', 'شصت', 'هفتاد', 'هشتاد', 'نود'];
  const hundreds = ['', 'صد', 'دویست', 'سیصد', 'چهارصد', 'پانصد', 'ششصد', 'هفتصد', 'هشتصد', 'نهصد'];
  const parts: string[] = [];
  const h = Math.floor(n / 100);
  const rem = n % 100;
  if (h) parts.push(hundreds[h]);
  if (rem >= 10 && rem <= 19) {
    parts.push(teens[rem - 10]);
  } else {
    const t = Math.floor(rem / 10);
    const o = rem % 10;
    if (t) parts.push(tens[t]);
    if (o) parts.push(ones[o]);
  }
  return parts.join(' و ');
};

const numberToPersianWords = (value: number): string => {
  if (!Number.isFinite(value) || value <= 0) return '';
  if (value === 0) return 'صفر';
  const units = ['', 'هزار', 'میلیون', 'میلیارد', 'تریلیون'];
  let n = Math.floor(value);
  let unitIndex = 0;
  const chunks: string[] = [];
  while (n > 0 && unitIndex < units.length) {
    const chunk = n % 1000;
    if (chunk > 0) {
      const chunkWords = threeDigitsToWords(chunk);
      chunks.unshift(chunkWords + (units[unitIndex] ? ` ${units[unitIndex]}` : ''));
    }
    n = Math.floor(n / 1000);
    unitIndex += 1;
  }
  return chunks.join(' و ');
};

type TabType = 'users' | 'mentors' | 'stores' | 'sales-managers' | 'sales-persons' | 'site-managers';

/**
 * نقش‌هایی که می‌شود به‌عنوان نقش افزوده داد، تا یک نفر هم‌زمان چند نقش
 * داشته باشد (مثلاً هم منتور، هم فروشگاه).
 *
 * «مدیر سایت» و «کاربر» عمداً اینجا نیستند: اولی دسترسی کامل است و نباید از
 * مسیر جانبی داده شود، دومی کف دسترسی همه است. همتای همین فهرست در سرور
 * ورودی را دوباره پالایش می‌کند.
 */
const EXTRA_ROLE_OPTIONS: { value: string; label: string }[] = [
  { value: 'MENTOR', label: 'منتور' },
  { value: 'STORE', label: 'فروشگاه' },
  { value: 'SALES_MANAGER', label: 'مدیر فروش' },
  { value: 'SALES_PERSON', label: 'فروشنده' },
];

const UsersManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [userCoursesCount, setUserCoursesCount] = useState<{[userId: string]: number}>({});
  const [userProductsCount, setUserProductsCount] = useState<{[userId: string]: number}>({});
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshTick, setRefreshTick] = useState(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isProductsModalOpen, setIsProductsModalOpen] = useState(false);
  const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);
  const [selectedUserForProducts, setSelectedUserForProducts] = useState<User | null>(null);
  const [selectedUserForWallet, setSelectedUserForWallet] = useState<User | null>(null);
  const [selectedUserProductsData, setSelectedUserProductsData] = useState<any>(null);
  const [walletAmount, setWalletAmount] = useState('');
  const [walletDescription, setWalletDescription] = useState('');
  const [walletMode, setWalletMode] = useState<'credit' | 'debit'>('credit');
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletSuccess, setWalletSuccess] = useState('');
  const [walletOverviewLoading, setWalletOverviewLoading] = useState(false);
  const [walletBalanceView, setWalletBalanceView] = useState(0);
  const [walletTransactions, setWalletTransactions] = useState<any[]>([]);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editingPassword, setEditingPassword] = useState('');
  const [editingConfirmPassword, setEditingConfirmPassword] = useState('');

  // Validation states
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [editFirstNameError, setEditFirstNameError] = useState('');
  const [editLastNameError, setEditLastNameError] = useState('');
  const [newUser, setNewUser] = useState({
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    role: 'USER' as 'ADMIN' | 'SALES_MANAGER' | 'SALES_PERSON' | 'MENTOR' | 'STORE' | 'USER',
    extraRoles: [] as string[],
    isActive: true,
    isForeign: false,
    selectedCourses: [] as string[],
  });
  const [editingUserCourses, setEditingUserCourses] = useState<string[]>([]);

  // Export filters and state
  const [exportFilters, setExportFilters] = useState({
    userType: 'all' as 'all' | 'old' | 'new',
    startDate: '',
    endDate: '',
    role: '',
  });
  const [exporting, setExporting] = useState(false);
  const [exportingJson, setExportingJson] = useState(false);
  const [exportingUserId, setExportingUserId] = useState<string | null>(null);

  const tabs = [
    { id: 'users' as TabType, label: 'کاربران', role: 'USER' },
    /* بدون این تب، کاربری که نقش منتور می‌گیرد از همهٔ فهرست‌ها ناپدید می‌شود */
    { id: 'mentors' as TabType, label: 'منتورها', role: 'MENTOR' },
    { id: 'stores' as TabType, label: 'فروشگاه‌ها', role: 'STORE' },
    { id: 'sales-managers' as TabType, label: 'مدیران فروش', role: 'SALES_MANAGER' },
    { id: 'sales-persons' as TabType, label: 'فروشنده‌ها', role: 'SALES_PERSON' },
    { id: 'site-managers' as TabType, label: 'مدیرهای سایت', role: 'ADMIN' },
  ];

  // جستجو با تأخیر (debounce) تا با هر حرف یا پاک‌کردن یا عوض کردن زبان کیبورد لود نشود
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const fetchData = async () => {
      const isSearching = !!debouncedSearchTerm.trim();
      const hasExistingData = users.length > 0;
      // وقتی جستجو خالی است و لیست از قبل داریم، فقط در پس‌زمینه رفرش می‌کنیم تا لودینگ نزنه
      if (isSearching) {
        setSearchLoading(true);
      } else if (!hasExistingData) {
        setLoading(true);
      }
      try {
        setError('');
        const roleFilter = tabs.find(t => t.id === activeTab)?.role;
        const [usersResponse, coursesResponse] = await Promise.all([
          usersService.getAll({
            page: currentPage,
            limit,
            search: debouncedSearchTerm.trim() || undefined,
            role: roleFilter,
          }),
          coursesService.getAll(),
        ]);
        
        setUsers(usersResponse.data);
        setTotal(usersResponse.meta.total);
        setTotalPages(usersResponse.meta.totalPages);
        setCourses(coursesResponse);
        
        const coursesCountData: {[userId: string]: number} = {};
        const productsCountData: {[userId: string]: number} = {};
        
        usersResponse.data.forEach((user: any) => {
          coursesCountData[user.id] = user._count?.purchasedCourses || 0;
          productsCountData[user.id] = user._count?.oldProducts || 0;
        });
        
        setUserCoursesCount(coursesCountData);
        setUserProductsCount(productsCountData);
      } catch (err: any) {
        setError(err.response?.data?.message || 'خطا در دریافت داده‌ها');
      } finally {
        setLoading(false);
        setSearchLoading(false);
      }
    };

    fetchData();
  }, [activeTab, currentPage, limit, debouncedSearchTerm, refreshTick]);
  
  // صفحه را فقط وقتی مقدار واقعی جستجو (بعد از debounce) عوض شد به ۱ برگردان
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, activeTab]);

  // Update export filters role when tab changes
  useEffect(() => {
    const currentRole = tabs.find(t => t.id === activeTab)?.role || '';
    setExportFilters(prev => ({
      ...prev,
      role: currentRole,
    }));
  }, [activeTab]);

  const handleDelete = async (id: string, userName: string) => {
    if (!window.confirm(`آیا از حذف "${userName}" اطمینان دارید؟\n\nاین عمل غیرقابل بازگشت است و تمام اطلاعات مربوط به این کاربر حذف خواهد شد.`)) {
      return;
    }

    try {
      await usersService.remove(id);
      setUsers(users.filter(user => user.id !== id));
      // Remove user courses and products counts from state
      setUserCoursesCount(prev => {
        const newCounts = { ...prev };
        delete newCounts[id];
        return newCounts;
      });
      setUserProductsCount(prev => {
        const newCounts = { ...prev };
        delete newCounts[id];
        return newCounts;
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در حذف کاربر');
    }
  };

  const handleExportUsers = async () => {
    setExporting(true);
    try {
      // Get current tab role
      const currentRole = tabs.find(t => t.id === activeTab)?.role || '';
      
      // Prepare filters with current role
      const filters = {
        ...exportFilters,
        role: currentRole || exportFilters.role,
      };
      
      const blob = await usersService.exportUsers(filters);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Generate filename
      const date = new Date().toISOString().split('T')[0];
      let filename = `users_export_${date}`;
      if (filters.userType !== 'all') {
        filename += `_${filters.userType}`;
      }
      if (filters.startDate || filters.endDate) {
        filename += `_${filters.startDate || 'start'}_${filters.endDate || 'end'}`;
      }
      if (filters.role) {
        filename += `_${filters.role}`;
      }
      filename += '.xlsx';
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'خطا در دانلود فایل Excel');
    } finally {
      setExporting(false);
    }
  };

  const handleExportUsersJson = async () => {
    setExportingJson(true);
    try {
      const currentRole = tabs.find(t => t.id === activeTab)?.role || '';
      const filters = {
        ...exportFilters,
        role: currentRole || exportFilters.role,
      };

      const blob = await usersService.exportUsersJson(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const date = new Date().toISOString().split('T')[0];
      let filename = `users_export_${date}`;
      if (filters.userType !== 'all') {
        filename += `_${filters.userType}`;
      }
      if (filters.startDate || filters.endDate) {
        filename += `_${filters.startDate || 'start'}_${filters.endDate || 'end'}`;
      }
      if (filters.role) {
        filename += `_${filters.role}`;
      }
      filename += '.json';

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'خطا در دانلود JSON');
    } finally {
      setExportingJson(false);
    }
  };

  // Validation functions
  const validateName = (name: string): string => {
    if (!name.trim()) return '';

    // Check if name contains numbers or phone-like patterns
    const hasNumbers = /\d/.test(name);
    const looksLikePhone = /^(\+98|0)?9\d{9}$/.test(name.replace(/\s/g, ''));

    if (hasNumbers || looksLikePhone) {
      return 'نام و نام خانوادگی نمی‌تواند شامل عدد یا شماره تلفن باشد';
    }

    // Check for very short names (less than 2 characters)
    if (name.trim().length < 2) {
      return 'نام و نام خانوادگی باید حداقل ۲ کاراکتر باشد';
    }

    return '';
  };

  const handleFirstNameChange = (firstName: string) => {
    const error = validateName(firstName);
    setFirstNameError(error);
    setNewUser({...newUser, firstName});
  };

  const handleLastNameChange = (lastName: string) => {
    const error = validateName(lastName);
    setLastNameError(error);
    setNewUser({...newUser, lastName});
  };

  const handleEditFirstNameChange = (firstName: string) => {
    const error = validateName(firstName);
    setEditFirstNameError(error);
    if (editingUser) {
      setEditingUser({...editingUser, firstName});
    }
  };

  const handleEditLastNameChange = (lastName: string) => {
    const error = validateName(lastName);
    setEditLastNameError(error);
    if (editingUser) {
      setEditingUser({...editingUser, lastName});
    }
  };

  const handleExportSingleUser = async (userId: string, userName: string) => {
    setExportingUserId(userId);
    try {
      const blob = await usersService.exportUserJson(userId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (userName || 'user').replace(/\s+/g, '_');
      a.download = `user_${safeName || userId}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'خطا در دانلود داده کاربر');
    } finally {
      setExportingUserId(null);
    }
  };

  // Phone validation function (matches backend normalizePhone logic)
  const validatePhone = (phone: string): boolean => {
    return isValidIranianPhone(phone);
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); // Clear any previous errors
    
    // Validate password for USER role
    /* برای منتور رمز اختیاری است، ولی اگر وارد شد باید همان قواعد را رعایت کند */
    if (newUser.role === 'USER' || (newUser.role === 'MENTOR' && newUser.password.trim())) {
      if (!newUser.password.trim()) {
        setError('رمز عبور الزامی است');
        return;
      }
      
      if (newUser.password.length < 6) {
        setError('رمز عبور باید حداقل ۶ کاراکتر باشد');
        return;
      }
      
      if (!newUser.confirmPassword.trim()) {
        setError('تأیید رمز عبور الزامی است');
        return;
      }
      
      if (newUser.password !== newUser.confirmPassword) {
        setError('رمز عبور و تأیید رمز عبور مطابقت ندارند');
        return;
      }
    }
    
    // Validate role-specific requirements
    if (newUser.role === 'ADMIN' && !newUser.email.trim()) {
      setError('مدیران باید ایمیل داشته باشند');
      return;
    }
    if (newUser.isForeign && !newUser.email.trim()) {
      setError('کاربر خارجی باید ایمیل داشته باشد');
      return;
    }
    if (newUser.role !== 'ADMIN' && !newUser.isForeign && !newUser.phone.trim()) {
      setError('کاربران غیرمدیر باید شماره تلفن داشته باشند');
      return;
    }

    // Validate phone number format for non-admin users (when phone is provided)
    if (newUser.role !== 'ADMIN' && !newUser.isForeign && newUser.phone.trim() && !validatePhone(newUser.phone)) {
      setError('فرمت شماره تلفن نامعتبر است. شماره باید با 0 شروع شود و 10 یا 11 رقم داشته باشد (مثال: 09123456789)');
      return;
    }
    
    try {
      const userData: any = {
        role: newUser.role,
        extraRoles: newUser.extraRoles.filter((r) => r !== newUser.role),
        isActive: newUser.isActive,
      };

      if (newUser.phone.trim()) userData.phone = normalizePhoneNumber(newUser.phone);
      if (newUser.email.trim()) userData.email = newUser.email.trim();
      if (newUser.firstName.trim()) userData.firstName = newUser.firstName.trim();
      if (newUser.lastName.trim()) userData.lastName = newUser.lastName.trim();
      if (
        (newUser.role === 'USER' || newUser.role === 'MENTOR') &&
        newUser.password.trim()
      ) {
        userData.password = newUser.password;
        userData.confirmPassword = newUser.confirmPassword;
      }
      if (newUser.isForeign) userData.isForeign = true;

      const createdUser = await usersService.create(userData);
      setUsers([...users, createdUser]);
      
      if (newUser.selectedCourses.length > 0) {
        await usersService.assignCourses(createdUser.id, newUser.selectedCourses);
        // Update courses count
        setUserCoursesCount(prev => ({
          ...prev,
          [createdUser.id]: newUser.selectedCourses.length
        }));
      } else {
        setUserCoursesCount(prev => ({
          ...prev,
          [createdUser.id]: 0
        }));
      }
      
      setIsModalOpen(false);
      setNewUser({
        phone: '',
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        role: 'USER',
        extraRoles: [],
        isActive: true,
        isForeign: false,
        selectedCourses: [],
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در ایجاد کاربر');
    }
  };

  const handleEditUser = async (user: User) => {
    setEditingUser(user);
    setEditingPassword('');
    setEditingConfirmPassword('');
    setIsEditModalOpen(true);
    
    try {
      const userCourses = await usersService.getUserCourses(user.id);
      const courseIds = userCourses
        .map((item: any) => {
          // If it's an enrollment object with a course property
          if (item.course && item.course.id) {
            return item.course.id;
          }
          // If it's directly a course object
          if (item.id) {
            return item.id;
          }
          return null;
        })
        .filter((id: any) => id !== null);
      setEditingUserCourses(courseIds);
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در دریافت دوره‌های کاربر');
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    
    setError(''); // Clear any previous errors

    // Validate Iranian phone format only when phone provided, not admin, and not foreign
    if (editingUser.role !== 'ADMIN' && !editingUser.isForeign && editingUser.phone && editingUser.phone.trim() && !validatePhone(editingUser.phone)) {
      setError('فرمت شماره تلفن نامعتبر است. شماره باید با 0 شروع شود و 10 یا 11 رقم داشته باشد (مثال: 09123456789)');
      return;
    }
    
    // Validate password if provided
    if (editingPassword || editingConfirmPassword) {
      if (!editingPassword.trim()) {
        setError('رمز عبور الزامی است');
        return;
      }
      
      if (editingPassword.length < 6) {
        setError('رمز عبور باید حداقل ۶ کاراکتر باشد');
        return;
      }
      
      if (!editingConfirmPassword.trim()) {
        setError('تأیید رمز عبور الزامی است');
        return;
      }
      
      if (editingPassword !== editingConfirmPassword) {
        setError('رمز عبور و تأیید رمز عبور مطابقت ندارند');
        return;
      }
    }
    
    try {
      const phoneTrimmed = editingUser.phone?.trim();
      const phoneValue = phoneTrimmed
        ? (editingUser.isForeign ? phoneTrimmed : normalizePhoneNumber(editingUser.phone || ''))
        : null;
      if (phoneTrimmed && !editingUser.isForeign && !phoneValue) {
        setError('فرمت شماره تلفن نامعتبر است.');
        return;
      }
      // Only send editable fields
      const updateData: any = {
        username: editingUser.username,
        phone: phoneValue,
        email: editingUser.email ?? '',
        firstName: editingUser.firstName,
        lastName: editingUser.lastName,
        isActive: editingUser.isActive,
        role: editingUser.role,
        extraRoles: (editingUser.extraRoles ?? []).filter((r) => r !== editingUser.role),
        isForeign: editingUser.isForeign,
      };
      
      // Add password if provided
      if (editingPassword) {
        updateData.password = editingPassword;
        updateData.confirmPassword = editingConfirmPassword;
      }
      
      const updatedUser = await usersService.update(editingUser.id, updateData);
      setUsers(users.map(user => user.id === editingUser.id ? updatedUser : user));
      
      await usersService.assignCourses(editingUser.id, editingUserCourses);
      
      setIsEditModalOpen(false);
      setEditingUser(null);
      setEditingPassword('');
      setEditingConfirmPassword('');
      setEditingUserCourses([]);
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در به‌روزرسانی کاربر');
    }
  };

  const handleBlockUser = async (userId: string) => {
    if (!window.confirm('آیا مطمئن هستید که می‌خواهید این کاربر را بلاک کنید؟')) {
      return;
    }
    
    try {
      const updatedUser = await usersService.blockUser(userId);
      setUsers(users.map(user => user.id === userId ? updatedUser : user));
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در بلاک کردن کاربر');
    }
  };

  const handleUnblockUser = async (userId: string) => {
    if (!window.confirm('آیا مطمئن هستید که می‌خواهید این کاربر را آنبلاک کنید؟')) {
      return;
    }
    
    try {
      const updatedUser = await usersService.unblockUser(userId);
      setUsers(users.map(user => user.id === userId ? updatedUser : user));
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در آنبلاک کردن کاربر');
    }
  };

  const handleViewProducts = async (user: User) => {
    setSelectedUserForProducts(user);
    setIsProductsModalOpen(true);
    
    try {
      const userData = await usersService.getUserWithProducts(user.id);
      setSelectedUserProductsData(userData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در دریافت محصولات کاربر');
      setSelectedUserProductsData(null);
    }
  };

  const openWalletModal = (user: User) => {
    setSelectedUserForWallet(user);
    setWalletAmount('');
    setWalletDescription('');
    setWalletMode('credit');
    setWalletSuccess('');
    setError('');
    setIsWalletModalOpen(true);
    setWalletOverviewLoading(true);
    setWalletBalanceView(Number(user.wallet?.balance || 0));
    setWalletTransactions([]);
    void (async () => {
      try {
        const overview = await paymentsService.getUserWalletOverview(user.id, 20);
        setWalletBalanceView(Number(overview?.balance || 0));
        setWalletTransactions(Array.isArray(overview?.transactions) ? overview.transactions : []);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'خطا در دریافت اطلاعات کیف پول کاربر');
      } finally {
        setWalletOverviewLoading(false);
      }
    })();
  };

  const handleWalletAdjust = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedUserForWallet) return;
    const amount = Number(normalizeDigits(walletAmount || ''));
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('مبلغ باید بیشتر از صفر باشد');
      return;
    }
    if (walletMode === 'debit' && amount > walletBalanceView) {
      setError('مبلغ کسر از موجودی فعلی کیف پول بیشتر است');
      return;
    }

    try {
      setWalletLoading(true);
      setError('');
      const response =
        walletMode === 'credit'
          ? await adminService.creditUserWallet(selectedUserForWallet.id, {
              amount,
              description: walletDescription.trim() || undefined,
            })
          : await adminService.debitUserWallet(selectedUserForWallet.id, {
              amount,
              description: walletDescription.trim() || undefined,
            });
      setWalletSuccess(
        `${response.message} | موجودی جدید: ${Number(response.walletBalance).toLocaleString('fa-IR')} تومان`,
      );
      setWalletBalanceView(Number(response.walletBalance || 0));
      setWalletAmount('');
      setWalletDescription('');
      const overview = await paymentsService.getUserWalletOverview(selectedUserForWallet.id, 20);
      setWalletTransactions(Array.isArray(overview?.transactions) ? overview.transactions : []);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          (walletMode === 'credit' ? 'خطا در افزایش اعتبار کیف پول' : 'خطا در کسر از کیف پول'),
      );
    } finally {
      setWalletLoading(false);
    }
  };

  const walletAmountNumber = Number(normalizeDigits(walletAmount || '')) || 0;
  const walletAmountWords = walletAmountNumber > 0 ? `${numberToPersianWords(walletAmountNumber)} تومان` : '';

  const getRoleBadge = (role: string) => {
    const roleConfig = {
      ADMIN: { text: 'مدیر سایت', color: 'bg-rose-100 text-rose-800 ring-1 ring-rose-200/80' },
      SALES_MANAGER: { text: 'مدیر فروش', color: 'bg-blue-100 text-blue-900 ring-1 ring-blue-200/80' },
      SALES_PERSON: { text: 'فروشنده', color: 'bg-emerald-100 text-emerald-900 ring-1 ring-emerald-200/80' },
      MENTOR: { text: 'منتور', color: 'bg-violet-100 text-violet-900 ring-1 ring-violet-200/80' },
      STORE: { text: 'فروشگاه', color: 'bg-amber-100 text-amber-900 ring-1 ring-amber-200/80' },
      USER: { text: 'کاربر', color: 'bg-slate-100 text-slate-800 ring-1 ring-slate-200/80' },
    };
    const config = roleConfig[role as keyof typeof roleConfig] || roleConfig.USER;
    return (
      <span className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-[11px] font-bold shadow-sm ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const getTabTitle = () => {
    const currentTab = tabs.find(tab => tab.id === activeTab);
    return currentTab?.label || 'کاربران';
  };

  const getAddButtonText = () => {
    const currentTab = tabs.find(tab => tab.id === activeTab);
    return currentTab?.label || 'کاربر';
  };

  const pageActiveCount = useMemo(
    () => users.filter((u) => u.isActive && !u.isBlocked).length,
    [users],
  );
  const pageAttentionCount = useMemo(
    () => users.filter((u) => !u.isActive || u.isBlocked).length,
    [users],
  );
  const activeFilterCount = useMemo(
    () => (debouncedSearchTerm.trim() ? 1 : 0),
    [debouncedSearchTerm],
  );

  const AddButton = () => (
    <button
      type="button"
      onClick={() => {
        setError('');
        setIsModalOpen(true);
      }}
      className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-black text-white shadow-[0_8px_24px_-12px_rgba(79,70,229,0.55)] ring-2 ring-indigo-500/25 transition hover:bg-indigo-700 active:scale-[0.99]"
    >
      <Plus className="h-5 w-5 shrink-0" strokeWidth={2.5} aria-hidden />
      <span>{getAddButtonText()} جدید</span>
    </button>
  );

  return (
    <div className="space-y-5">
      <div className="rounded-2xl bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-xl font-black text-gray-900 sm:text-2xl">مدیریت کاربران</h1>
            <p className="mt-1.5 max-w-2xl text-sm font-medium leading-relaxed text-gray-600">
              نقش‌ها، دسترسی و لیست کاربران را مانند ماژول لیدها با فیلتر و خروجی ساختاریافته مدیریت کنید.
            </p>
          </div>
          <AddButton />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl border border-blue-100 bg-gradient-to-b from-blue-50 to-white p-4 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold text-blue-800">کل {getTabTitle()}</p>
              <p className="mt-2 text-2xl font-black text-blue-950 tabular-nums">
                {(total || 0).toLocaleString('fa-IR')}
              </p>
              <p className="mt-1 text-[11px] font-semibold text-blue-700/85">با فیلتر جاری</p>
            </div>
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
              <FontAwesomeIcon icon={faUsers} className="h-4 w-4" />
            </span>
          </div>
        </div>
        <div className="rounded-2xl border border-indigo-100 bg-gradient-to-b from-indigo-50 to-white p-4 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold text-indigo-800">موقعیت صفحه</p>
              <p className="mt-2 text-2xl font-black text-indigo-950 tabular-nums">{currentPage.toLocaleString('fa-IR')}</p>
              <p className="mt-1 text-[11px] font-semibold text-indigo-700/90">
                از {Math.max(totalPages, 1).toLocaleString('fa-IR')} صفحه · {users.length.toLocaleString('fa-IR')} ردیف
              </p>
            </div>
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700">
              <Layers className="h-5 w-5" strokeWidth={2} aria-hidden />
            </span>
          </div>
        </div>
        <div className="rounded-2xl border border-emerald-100 bg-gradient-to-b from-emerald-50 to-white p-4 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold text-emerald-800">فعال در این صفحه</p>
              <p className="mt-2 text-2xl font-black text-emerald-950 tabular-nums">{pageActiveCount.toLocaleString('fa-IR')}</p>
              <p className="mt-1 text-[11px] font-semibold text-emerald-800/85">بدون بلاک</p>
            </div>
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
              <FontAwesomeIcon icon={faUserCheck} className="h-4 w-4" />
            </span>
          </div>
        </div>
        <div className="rounded-2xl border border-amber-100 bg-gradient-to-b from-amber-50 to-white p-4 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold text-amber-900">نیازمند توجه</p>
              <p className="mt-2 text-2xl font-black text-amber-950 tabular-nums">{pageAttentionCount.toLocaleString('fa-IR')}</p>
              <p className="mt-1 text-[11px] font-semibold text-amber-800/90">غیرفعال یا بلاک در همین صفحه</p>
            </div>
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-800">
              <FontAwesomeIcon icon={faUserSlash} className="h-4 w-4" />
            </span>
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-100 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-100 pb-4">
          <div>
            <h2 className="text-lg font-black text-gray-900">خروجی داده‌ها</h2>
            <p className="mt-1 text-sm font-medium text-gray-600">Excel برای گزارش و JSON برای مهاجرت یا بکاپ.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleExportUsers}
              disabled={exporting}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-black text-white shadow-sm ring-2 ring-emerald-500/25 transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-55"
            >
              {exporting ? (
                <span className="inline-flex items-center gap-2">
                  <BrandSpinnerRing size="xs" inverse />
                  در حال تولید…
                </span>
              ) : (
                <>
                  <Download className="h-4 w-4 shrink-0" strokeWidth={2.5} aria-hidden />
                  دانلود Excel
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleExportUsersJson}
              disabled={exportingJson}
              className="inline-flex items-center gap-2 rounded-xl border-2 border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-black text-amber-900 shadow-sm transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-55"
            >
              {exportingJson ? (
                <span className="inline-flex items-center gap-2">
                  <BrandSpinnerRing size="xs" />
                  JSON…
                </span>
              ) : (
                <>
                  <FileJson className="h-4 w-4 shrink-0" strokeWidth={2.5} aria-hidden />
                  دانلود JSON
                </>
              )}
            </button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div>
            <label className={formLabelCls}>نوع کاربر</label>
            <select
              value={exportFilters.userType}
              onChange={(e) => setExportFilters({ ...exportFilters, userType: e.target.value as 'all' | 'old' | 'new' })}
              className={formInputCls}
            >
              <option value="all">همه</option>
              <option value="old">قدیمی</option>
              <option value="new">جدید</option>
            </select>
          </div>
          <div>
            <label className={formLabelCls}>از تاریخ</label>
            <input
              type="date"
              value={exportFilters.startDate}
              onChange={(e) => setExportFilters({ ...exportFilters, startDate: e.target.value })}
              className={formInputCls}
            />
          </div>
          <div>
            <label className={formLabelCls}>تا تاریخ</label>
            <input
              type="date"
              value={exportFilters.endDate}
              onChange={(e) => setExportFilters({ ...exportFilters, endDate: e.target.value })}
              className={formInputCls}
            />
          </div>
          <div>
            <label className={formLabelCls}>نقش در خروجی (تب جاری)</label>
            <input
              type="text"
              value={tabs.find((t) => t.id === activeTab)?.label || ''}
              disabled
              readOnly
              className={`${formInputCls} cursor-not-allowed bg-slate-50 text-slate-600`}
            />
          </div>
        </div>

        <p className="mt-3 rounded-xl bg-indigo-50 px-3 py-2 text-xs font-semibold leading-relaxed text-indigo-900 ring-1 ring-indigo-100">
          خروجی بر اساس تب «{tabs.find((t) => t.id === activeTab)?.label}» و بازه تاریخ و نوع کاربر بالا اعمال می‌شود.
        </p>
      </div>

      <div className="space-y-4 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-100 sm:p-5">
        <div className="flex flex-col gap-3 border-b border-slate-100 pb-4 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h2 className="text-base font-black text-gray-900">فیلتر و جستجو</h2>
            <p className="mt-1 text-xs font-medium text-slate-600">
              نقش و متن جستجو؛ بروزرسانی دستی مشابه ماژول لیدها.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {activeFilterCount > 0 ? (
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600">
                {activeFilterCount.toLocaleString('fa-IR')} فیلتر فعال
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => setRefreshTick((t) => t + 1)}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-700"
            >
              <RefreshCw className="h-4 w-4 shrink-0" aria-hidden />
              بروزرسانی
            </button>
            <button
              type="button"
              onClick={() => {
                setSearchTerm('');
                setCurrentPage(1);
              }}
              disabled={!searchTerm.trim()}
              className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-45"
            >
              پاک‌کردن جستجو
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`rounded-full border px-3 py-1.5 text-xs font-bold shadow-sm transition active:scale-[0.98] ${
                activeTab === tab.id
                  ? 'border-indigo-500 bg-indigo-600 text-white shadow-indigo-500/20'
                  : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="relative min-w-0 flex-1 lg:max-w-xl">
            <Search className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" strokeWidth={2} aria-hidden />
            <input
              type="search"
              placeholder={`جستجو در ${getTabTitle()} (نام، موبایل، نام کاربری)…`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`${formInputCls} ps-11 pe-10`}
              dir="rtl"
            />
            {searchLoading ? (
              <div className="absolute left-3 top-1/2 flex -translate-y-1/2 items-center">
                <BrandSpinnerRing size="sm" />
              </div>
            ) : null}
          </div>
          {!loading && total > 0 ? (
            <div className="inline-flex shrink-0 items-center gap-3 rounded-xl border border-indigo-100 bg-gradient-to-l from-white to-indigo-50 px-4 py-2 shadow-sm ring-1 ring-indigo-100">
              <UsersRound className="h-5 w-5 text-indigo-600" strokeWidth={2} aria-hidden />
              <div className="text-sm">
                <p className="text-xs font-semibold text-slate-600">جمع کل فیلتر</p>
                <p className="text-lg font-black text-indigo-800 tabular-nums">{total.toLocaleString('fa-IR')}</p>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-100 sm:p-5">
        {loading && users.length === 0 ? (
          <div className="overflow-hidden rounded-2xl border border-indigo-100 bg-gradient-to-b from-indigo-50/60 via-white to-white">
            <div className="flex items-center justify-between border-b border-indigo-100/70 px-4 py-3">
              <div className="flex items-center gap-2 text-indigo-700">
                <BrandSpinnerRing size="sm" />
                <span className="text-sm font-bold">در حال بارگذاری کاربران</span>
              </div>
              <div className="flex items-center gap-1.5" aria-hidden>
                <span className="h-2 w-2 animate-bounce rounded-full bg-indigo-500" />
                <span className="h-2 w-2 animate-bounce rounded-full bg-violet-500 [animation-delay:120ms]" />
                <span className="h-2 w-2 animate-bounce rounded-full bg-fuchsia-500 [animation-delay:240ms]" />
              </div>
            </div>
            <div className="space-y-2 p-3">
              {Array.from({ length: 6 }).map((_, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-12 items-center gap-2 rounded-xl border border-slate-100 bg-white px-3 py-2.5 animate-pulse"
                >
                  <div className="col-span-2 h-4 rounded bg-slate-200" />
                  <div className="col-span-2 h-4 rounded bg-slate-200" />
                  <div className="col-span-2 h-4 rounded bg-slate-200" />
                  <div className="col-span-2 h-4 rounded bg-slate-200" />
                  <div className="col-span-1 h-4 rounded bg-slate-200" />
                  <div className="col-span-2 h-4 rounded bg-slate-200" />
                  <div className="col-span-1 h-8 rounded-lg bg-indigo-100/70" />
                </div>
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="flex gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
            <Shield className="h-5 w-5 shrink-0 text-red-500" strokeWidth={2} aria-hidden />
            <p className="text-sm font-semibold leading-relaxed text-red-900">{error}</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-slate-200/90 shadow-sm ring-1 ring-slate-100">
            <div className="overflow-x-auto">
              <table className="w-full table-fixed divide-y divide-slate-200/80 text-sm">
            <thead className="bg-gradient-to-b from-slate-100/90 to-slate-50/95">
              <tr>
                <th className="w-48 px-4 py-3.5 text-right text-xs font-bold tracking-wide text-slate-700">{getTabTitle()}</th>
                <th className="w-24 px-3 py-3.5 text-right text-xs font-bold tracking-wide text-slate-700">
                  نقش
                </th>
                <th className="w-36 px-3 py-3.5 text-right text-xs font-bold tracking-wide text-slate-700">
                  تماس
                </th>
                <th className="w-28 px-3 py-3.5 text-center text-xs font-bold tracking-wide text-slate-700">
                  دوره‌ها
                </th>
                <th className="w-28 px-3 py-3.5 text-center text-xs font-bold tracking-wide text-slate-700">
                  محصولات <span className="text-red-700">قدیمی</span>
                </th>
                <th className="w-24 px-3 py-3.5 text-center text-xs font-bold tracking-wide text-slate-700">
                  وضعیت
                </th>
                <th className="w-28 px-3 py-3.5 text-right text-xs font-bold tracking-wide text-slate-700">
                  تاریخ
                </th>
                <th className="w-28 px-3 py-3.5 text-center text-xs font-bold tracking-wide text-slate-700">
                  عملیات
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200/70 bg-white">
              {users.map((user, rowIndex) => (
                <tr
                  key={user.id}
                  className={`transition-colors duration-150 hover:bg-indigo-50/70 ${
                    rowIndex % 2 === 1 ? 'bg-slate-50/45' : 'bg-white'
                  }`}
                >
                  <td className="px-4 py-4 align-middle">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 text-xs font-black leading-none text-white shadow-md shadow-indigo-900/15 ring-2 ring-white">
                        {user.firstName?.[0] || user.phone?.[0] || '•'}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold leading-snug text-slate-900">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="truncate text-[11px] font-medium leading-snug text-slate-500">@{user.username}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 align-middle">
                    {/* نقش اصلی و بعد نقش‌های افزوده، تا چندنقشی‌بودن در فهرست پیدا باشد */}
                    <div className="flex flex-wrap items-center gap-1">
                      {getRoleBadge(user.role)}
                      {(user.extraRoles ?? []).map((extra) => (
                        <span key={extra}>{getRoleBadge(extra)}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-4 align-middle">
                    <div className="truncate text-sm font-medium tabular-nums text-slate-900" title={user.phone}>
                      {user.phone}
                    </div>
                    <div className="mt-0.5 truncate text-[11px] leading-snug text-slate-500" title={user.email || ''}>
                      {user.email || '—'}
                    </div>
                  </td>
                  <td className="px-3 py-4 text-center align-middle">
                    {userCoursesCount[user.id] > 0 ? (
                      <button
                        type="button"
                        onClick={() => handleViewProducts(user)}
                        className="inline-flex min-h-[2rem] min-w-[2rem] items-center justify-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-900 ring-1 ring-blue-200/80 transition hover:bg-blue-100 hover:ring-blue-300/60"
                      >
                        {userCoursesCount[user.id]}
                      </button>
                    ) : (
                      <span className="inline-flex min-h-[2rem] min-w-[2rem] items-center justify-center rounded-lg bg-slate-50 text-xs font-semibold text-slate-400 ring-1 ring-slate-100">
                        —
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-4 text-center align-middle">
                    {user.isOld && userProductsCount[user.id] > 0 ? (
                      <button
                        type="button"
                        onClick={() => handleViewProducts(user)}
                        className="inline-flex min-h-[2rem] min-w-[2rem] items-center justify-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-900 ring-1 ring-amber-200/90 transition hover:bg-amber-100"
                      >
                        {userProductsCount[user.id]}
                      </button>
                    ) : user.isOld ? (
                      <button
                        type="button"
                        onClick={() => handleViewProducts(user)}
                        className="text-xs font-semibold text-orange-700 underline-offset-2 transition hover:text-orange-900 hover:underline"
                      >
                        نمایش
                      </button>
                    ) : (
                      <span className="inline-flex min-h-[2rem] min-w-[2rem] items-center justify-center rounded-lg bg-slate-50 text-xs font-semibold text-slate-400 ring-1 ring-slate-100">
                        —
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-4 text-center align-middle">
                    <div className="flex flex-col items-center justify-center gap-1.5">
                      <span
                        className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ${
                          user.isActive
                            ? 'bg-emerald-50 text-emerald-800 ring-emerald-200/90'
                            : 'bg-rose-50 text-rose-800 ring-rose-200/90'
                        }`}
                      >
                        {user.isActive ? 'فعال' : 'غیرفعال'}
                      </span>
                      {user.isBlocked ? (
                        <span className="inline-flex max-w-[7.5rem] flex-col items-center rounded-full bg-orange-50 px-2 py-1 text-[10px] font-bold leading-tight text-orange-900 ring-1 ring-orange-200/90">
                          بلاک
                          {user.blockedUntil ? (
                            <span className="mt-0.5 font-medium text-orange-800/90">
                              تا {new Date(user.blockedUntil).toLocaleDateString('fa-IR')}
                            </span>
                          ) : null}
                        </span>
                      ) : null}
                      {Number(user.rateLimitViolations) > 0 ? (
                        <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 ring-1 ring-slate-200/80">
                          {Number(user.rateLimitViolations).toLocaleString('fa-IR')} تخلف
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-4 align-middle text-sm font-medium tabular-nums text-slate-600">
                    {new Date(user.createdAt).toLocaleDateString('fa-IR')}
                  </td>
                  <td className="px-2 py-4 align-middle">
                    <div className="flex flex-wrap items-center justify-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleExportSingleUser(user.id, `${user.firstName} ${user.lastName}`.trim() || user.username)}
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-amber-200/90 bg-white text-amber-700 shadow-sm transition hover:border-amber-300 hover:bg-amber-50 hover:text-amber-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/35 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-45"
                        title="دانلود داده کاربر"
                        disabled={exportingUserId === user.id}
                      >
                        {exportingUserId === user.id ? (
                          <BrandSpinnerRing size="sm" />
                        ) : (
                          <svg className="h-[18px] w-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 16v-4m0 0V8m0 4h4m-4 0H8m4 8a8 8 0 110-16 8 8 0 010 16z" />
                          </svg>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => openWalletModal(user)}
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-emerald-200/90 bg-white text-emerald-700 shadow-sm transition hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/35 active:scale-[0.97]"
                        title="مدیریت کیف پول"
                      >
                        <svg className="h-[18px] w-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a4 4 0 00-8 0v2m-3 0h14a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2v-7a2 2 0 012-2z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleEditUser(user)}
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-indigo-200/90 bg-white text-indigo-700 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/35 active:scale-[0.97]"
                        title="ویرایش"
                      >
                        <span className="scale-90 [&>svg]:h-[18px] [&>svg]:w-[18px]">
                          <EditIcon />
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(user.id, `${user.firstName} ${user.lastName}`)}
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-rose-200/90 bg-white text-rose-700 shadow-sm transition hover:border-rose-300 hover:bg-rose-50 hover:text-rose-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/35 active:scale-[0.97]"
                        title="حذف"
                      >
                        <span className="scale-90 [&>svg]:h-[18px] [&>svg]:w-[18px]">
                          <DeleteIcon />
                        </span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {users.length === 0 && !loading && (
          <div className="py-12 text-center">
            <EmptyState
              icon={<PeopleIcon />}
              title="کاربری یافت نشد"
              description={`${getTabTitle()}ی با این فیلترها یافت نشد.`}
            />
          </div>
        )}
          </div>
        )}
      </div>
      
      {/* Pagination */}
      {totalPages > 1 ? (
        <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:py-3.5">
          <div className="text-sm font-semibold text-slate-700">
            نمایش <span className="font-black text-indigo-800">{((currentPage - 1) * limit + 1).toLocaleString('fa-IR')}</span>{' '}
            تا{' '}
            <span className="font-black text-indigo-800">{Math.min(currentPage * limit, total).toLocaleString('fa-IR')}</span>{' '}
            از <span className="font-black text-slate-900">{total.toLocaleString('fa-IR')}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-800 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-45"
            >
              قبلی
            </button>
            <div className="flex flex-wrap gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum = i + 1;
                if (totalPages > 5) {
                  if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    type="button"
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`min-w-[2.75rem] rounded-xl px-3 py-2 text-sm font-black tabular-nums transition ${
                      currentPage === pageNum
                        ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/35 ring-2 ring-indigo-500/30'
                        : 'border border-slate-200 bg-white text-slate-800 hover:bg-slate-50'
                    }`}
                  >
                    {pageNum.toLocaleString('fa-IR')}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-800 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-45"
            >
              بعدی
            </button>
          </div>
        </div>
      ) : null}
      
      {/* Products Modal */}
      <Modal
        isOpen={isProductsModalOpen}
        onClose={() => {
          setIsProductsModalOpen(false);
          setSelectedUserForProducts(null);
          setSelectedUserProductsData(null);
        }}
        title={`محصولات و دوره‌های ${selectedUserForProducts?.firstName} ${selectedUserForProducts?.lastName}`}
        size="large"
      >
        {selectedUserProductsData ? (
          <div className="space-y-6">
            {/* Old Products */}
            {selectedUserProductsData.oldProducts && selectedUserProductsData.oldProducts.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="w-2 h-2 bg-orange-500 rounded-full ml-2"></span>
                  محصولات <span className="text-red-600 font-bold mx-1">قدیمی</span> ({selectedUserProductsData.oldProducts.length})
                </h3>
                <div className="space-y-2">
                  {selectedUserProductsData.oldProducts.map((product: any, idx: number) => (
                    <div key={product.id || idx} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium text-gray-900">{product.productName || product.name || 'محصول بدون نام'}</h4>
                          {product.productCategory && (
                            <p className="text-sm text-gray-500 mt-1">دسته: {product.productCategory}</p>
                          )}
                          {product.productId && (
                            <p className="text-xs text-gray-400 mt-1">شناسه: {product.productId}</p>
                          )}
                        </div>
                        {product.importedAt && (
                          <span className="text-xs text-gray-400">
                            {new Date(product.importedAt).toLocaleDateString('fa-IR')}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Purchased Courses */}
            {selectedUserProductsData.purchasedCourses && selectedUserProductsData.purchasedCourses.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full ml-2"></span>
                  دوره‌های خریداری شده ({selectedUserProductsData.purchasedCourses.length})
                </h3>
                <div className="space-y-4">
                  {selectedUserProductsData.purchasedCourses.map((enrollment: any) => (
                    <div key={enrollment.id || enrollment.course?.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      {enrollment.course && (
                        <>
                          <div className="bg-gray-50 p-4 border-b border-gray-200">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <h4 className="font-medium text-gray-900 text-lg">{enrollment.course.title}</h4>
                                {enrollment.course.description && (
                                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{enrollment.course.description}</p>
                                )}
                              </div>
                              <div className="text-left mr-4 flex-shrink-0">
                                {enrollment.course.price && (
                                  <span className="text-sm font-medium text-blue-600 whitespace-nowrap">
                                    {enrollment.course.price.toLocaleString()} تومان
                                  </span>
                                )}
                                {enrollment.enrolledAt && (
                                  <p className="text-xs text-gray-400 mt-1 whitespace-nowrap">
                                    {new Date(enrollment.enrolledAt).toLocaleDateString('fa-IR')}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="p-4">
                            {/* Videos Section */}
                            {enrollment.course.videos && enrollment.course.videos.length > 0 && (
                              <div className="mb-4">
                                <h5 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                  ویدیوهای دوره ({enrollment.course.videos.length})
                                </h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                  {enrollment.course.videos.map((video: any, idx: number) => (
                                    <div key={video.id} className="flex items-center gap-2 p-2 bg-blue-50 rounded text-sm">
                                      <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs">
                                        {idx + 1}
                                      </span>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-gray-900 truncate">{video.title}</p>
                                        {video.duration && (
                                          <p className="text-xs text-gray-500">
                                            {Math.floor(video.duration / 60)} دقیقه
                                          </p>
                                        )}
                                      </div>
                                      <span className={`text-xs px-2 py-1 rounded ${video.published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                        {video.published ? 'منتشر شده' : 'پیش‌نویس'}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Attachments Section */}
                            {enrollment.course.attachments && enrollment.course.attachments.length > 0 && (
                              <div>
                                <h5 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                  </svg>
                                  فایل‌های ضمیمه ({enrollment.course.attachments.length})
                                </h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                  {enrollment.course.attachments.map((attachment: string, idx: number) => {
                                    const fileName = attachment.split('/').pop() || attachment;
                                    const fileExt = fileName.split('.').pop()?.toLowerCase() || '';
                                    return (
                                      <a 
                                        key={idx}
                                        href={attachment}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 p-2 bg-green-50 rounded text-sm hover:bg-green-100 transition-colors"
                                      >
                                        <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <div className="flex-1 min-w-0">
                                          <p className="text-gray-900 truncate">{fileName}</p>
                                          <p className="text-xs text-gray-500 uppercase">{fileExt}</p>
                                        </div>
                                      </a>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            
                            {(!enrollment.course.videos || enrollment.course.videos.length === 0) && 
                             (!enrollment.course.attachments || enrollment.course.attachments.length === 0) && (
                              <p className="text-sm text-gray-500 text-center py-4">
                                هیچ محتوای اضافی برای این دوره موجود نیست
                              </p>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {(!selectedUserProductsData.oldProducts || selectedUserProductsData.oldProducts.length === 0) &&
             (!selectedUserProductsData.purchasedCourses || selectedUserProductsData.purchasedCourses.length === 0) && (
              <div className="text-center py-8">
                <p className="text-gray-500">این کاربر محصول یا دوره‌ای ندارد.</p>
              </div>
            )}
          </div>
        ) : (
          <div className="py-6">
            <SiteBrandedLoader variant="section" message="در حال بارگذاری..." className="min-h-0 bg-transparent py-0" />
          </div>
        )}
      </Modal>
      
      {users.length === 0 && loading && (
          <EmptyState
            icon={
              <svg className="h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            }
            title={`${getTabTitle()}ی یافت نشد`}
            description={searchTerm ? `هیچ ${getTabTitle()}ی با این مشخصات یافت نشد.` : `هنوز ${getTabTitle()}ی ثبت نشده است.`}
            action={!searchTerm ? <AddButton /> : undefined}
          />
        )}
      
      <Modal
        isOpen={isWalletModalOpen}
        onClose={() => {
          setIsWalletModalOpen(false);
          setSelectedUserForWallet(null);
          setWalletSuccess('');
        }}
        title={`مدیریت کیف پول ${selectedUserForWallet?.firstName || ''} ${selectedUserForWallet?.lastName || ''}`}
      >
        <form onSubmit={handleWalletAdjust} className="space-y-4">
          <div className="flex rounded-xl border border-slate-200 bg-slate-50/90 p-1">
            <button
              type="button"
              onClick={() => {
                setWalletMode('credit');
                setWalletSuccess('');
                setError('');
              }}
              className={`flex-1 rounded-lg py-2 text-xs font-black transition sm:text-sm ${
                walletMode === 'credit'
                  ? 'bg-white text-emerald-700 shadow-sm ring-1 ring-emerald-200/80'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              افزایش اعتبار
            </button>
            <button
              type="button"
              onClick={() => {
                setWalletMode('debit');
                setWalletSuccess('');
                setError('');
              }}
              className={`flex-1 rounded-lg py-2 text-xs font-black transition sm:text-sm ${
                walletMode === 'debit'
                  ? 'bg-white text-rose-700 shadow-sm ring-1 ring-rose-200/80'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              کسر از کیف پول
            </button>
          </div>
          <div className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2.5">
            <p className="text-xs font-semibold text-blue-700">اعتبار فعلی کیف پول</p>
            <p className="mt-1 text-lg font-black text-blue-900">
              {Number(walletBalanceView || 0).toLocaleString('fa-IR')} تومان
            </p>
          </div>
          {walletSuccess ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700">
              {walletSuccess}
            </div>
          ) : null}
          <div>
            <label className={formLabelCls}>
              {walletMode === 'credit' ? 'مبلغ افزایش (تومان)' : 'مبلغ کسر (تومان)'}
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={formatThousands(walletAmount)}
              onChange={(e) => setWalletAmount(normalizeDigits(e.target.value))}
              className={formInputCls}
              placeholder="مثال: 500000"
              required
            />
            <p className="mt-1 px-1 text-[11px] font-medium text-slate-500">
              {walletAmountWords || ' '}
            </p>
          </div>
          <div>
            <label className={formLabelCls}>توضیحات (اختیاری)</label>
            <textarea
              value={walletDescription}
              onChange={(e) => setWalletDescription(e.target.value)}
              className={formInputCls}
              rows={3}
              placeholder={
                walletMode === 'credit' ? 'علت افزایش اعتبار را وارد کنید' : 'علت کسر از کیف پول را وارد کنید'
              }
            />
          </div>
          <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={() => setIsWalletModalOpen(false)}
              className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-bold text-slate-800 shadow-sm transition hover:bg-slate-50"
            >
              بستن
            </button>
            <button
              type="submit"
              disabled={walletLoading}
              className={`rounded-xl px-5 py-2.5 text-sm font-black text-white shadow-md transition disabled:opacity-60 ${
                walletMode === 'credit'
                  ? 'bg-emerald-600 shadow-emerald-600/35 hover:bg-emerald-700'
                  : 'bg-rose-600 shadow-rose-600/35 hover:bg-rose-700'
              }`}
            >
              {walletLoading ? 'در حال ثبت...' : walletMode === 'credit' ? 'افزایش اعتبار' : 'ثبت کسر'}
            </button>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <h4 className="mb-2 text-sm font-black text-slate-800">آخرین تراکنش‌های کیف پول کاربر</h4>
            {walletOverviewLoading ? (
              <div className="py-5 text-center">
                <BrandSpinnerRing size="sm" />
              </div>
            ) : walletTransactions.length === 0 ? (
              <p className="text-xs text-slate-500">تراکنشی برای این کیف پول ثبت نشده است.</p>
            ) : (
              <div className="max-h-64 space-y-2 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-2">
                {walletTransactions.map((tx) => (
                  <div key={tx.id} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-black text-slate-900">
                        {tx.type === 'WALLET_CHARGE' ? 'شارژ کیف پول' : tx.type === 'WALLET_DEDUCTION' ? 'کسر از کیف پول' : tx.type}
                      </span>
                      <span className={`rounded-full px-2 py-0.5 font-bold ${
                        tx.status === 'PAID' ? 'bg-emerald-100 text-emerald-700' : tx.status === 'FAILED' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {tx.status === 'PAID' ? 'موفق' : tx.status === 'FAILED' ? 'ناموفق' : tx.status}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center justify-between text-slate-600">
                      <span>مبلغ: {Number(tx.amount || 0).toLocaleString('fa-IR')} تومان</span>
                      <span>{new Date(tx.createdAt).toLocaleDateString('fa-IR')}</span>
                    </div>
                    <div className="mt-1 text-slate-500">
                      مانده: {Number(tx.balanceAfter || 0).toLocaleString('fa-IR')} تومان
                    </div>
                    {tx.description ? <p className="mt-1 text-slate-500">{tx.description}</p> : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={`${getAddButtonText()} جدید`}
      >
        <form onSubmit={handleAddUser} className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
            <label className={formLabelCls}>
              نام
            </label>
            <input
              type="text"
              value={newUser.firstName}
              onChange={(e) => handleFirstNameChange(e.target.value)}
              className={`${formInputCls} ${firstNameError ? formInputErrorCls : ''}`}
              required
            />
            {firstNameError && (
              <p className="text-sm text-red-600 mt-1">{firstNameError}</p>
            )}
          </div>
          <div>
            <label className={formLabelCls}>
              نام خانوادگی
            </label>
            <input
              type="text"
              value={newUser.lastName}
              onChange={(e) => handleLastNameChange(e.target.value)}
              className={`${formInputCls} ${lastNameError ? formInputErrorCls : ''}`}
            />
            {lastNameError && (
              <p className="text-sm text-red-600 mt-1">{lastNameError}</p>
            )}
            </div>
          </div>
          <div>
            <label className={formLabelCls}>
              شماره تلفن {newUser.role !== 'ADMIN' && !newUser.isForeign && <span className="text-red-500">*</span>}
            </label>
            <input
              type="tel"
              value={newUser.phone}
              onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
              className={formInputCls}
              required={newUser.role !== 'ADMIN' && !newUser.isForeign}
              disabled={newUser.role === 'ADMIN'}
            />
            {newUser.role === 'ADMIN' && (
              <p className="text-xs text-gray-500 mt-1">مدیران فقط با ایمیل وارد می‌شوند</p>
            )}
            {newUser.isForeign && (
              <p className="text-xs text-gray-500 mt-1">برای کاربر خارجی شماره اختیاری است</p>
            )}
          </div>
          <div>
            <label className={formLabelCls}>
              ایمیل {(newUser.role === 'ADMIN' || newUser.isForeign) && <span className="text-red-500">*</span>}
            </label>
            <input
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({...newUser, email: e.target.value})}
              className={formInputCls}
              required={newUser.role === 'ADMIN' || newUser.isForeign}
              disabled={newUser.role !== 'ADMIN' && !newUser.isForeign}
            />
            {newUser.role === 'ADMIN' && !newUser.isForeign && (
              <p className="text-xs text-gray-500 mt-1">مدیران فقط با ایمیل وارد می‌شوند</p>
            )}
            {newUser.isForeign && (
              <p className="text-xs text-gray-500 mt-1">کاربر خارجی با ایمیل و رمز عبور وارد می‌شود</p>
            )}
            {newUser.role !== 'ADMIN' && !newUser.isForeign && (
              <p className="text-xs text-gray-500 mt-1">کاربران عادی فقط با شماره تلفن وارد می‌شوند</p>
            )}
          </div>
          {(newUser.role === 'USER' || newUser.role === 'MENTOR') && (
            <>
              {newUser.role === 'MENTOR' ? (
                /* منتور با رمز وارد پنل می‌شود؛ بدون رمز فقط سایت اصلی را دارد */
                <p className="sm:col-span-2 rounded-lg bg-violet-50 px-3 py-2 text-xs font-bold text-violet-800">
                  ورود به پنل ادمین با رمز عبور است. اگر رمز نگذارید، منتور فقط
                  می‌تواند با کد پیامکی وارد سایت اصلی شود و به پنل دسترسی ندارد.
                </p>
              ) : null}
              <div>
                <label className={formLabelCls}>
                  رمز عبور{' '}
                  {newUser.role === 'USER' ? (
                    <span className="text-red-500">*</span>
                  ) : (
                    <span className="text-gray-400">(اختیاری)</span>
                  )}
                </label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  className={formInputCls}
                  required
                  minLength={6}
                  placeholder="حداقل ۶ کاراکتر"
                />
              </div>
              <div>
                <label className={formLabelCls}>
                  تأیید رمز عبور <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={newUser.confirmPassword}
                  onChange={(e) => setNewUser({...newUser, confirmPassword: e.target.value})}
                  className={formInputCls}
                  required
                  minLength={6}
                  placeholder="تأیید رمز عبور"
                />
              </div>
            </>
          )}
          <div>
            <label className={formLabelCls}>
              نقش
            </label>
            <select
              value={newUser.role}
              onChange={(e) => setNewUser({...newUser, role: e.target.value as 'ADMIN' | 'SALES_MANAGER' | 'SALES_PERSON' | 'MENTOR' | 'STORE' | 'USER'})}
              className={formInputCls}
            >
              <option value="USER">کاربر</option>
              <option value="SALES_PERSON">فروشنده</option>
              <option value="MENTOR">منتور</option>
              <option value="STORE">فروشگاه</option>
              <option value="SALES_MANAGER">مدیر فروش</option>
              <option value="ADMIN">مدیر سایت</option>
            </select>
            <div className="mt-3">
              <label className={formLabelCls}>نقش‌های افزوده (اختیاری)</label>
              <div className="flex flex-wrap gap-2">
                {EXTRA_ROLE_OPTIONS.filter((opt) => opt.value !== newUser.role).map((opt) => {
                  const checked = newUser.extraRoles.includes(opt.value);
                  return (
                    <label
                      key={opt.value}
                      className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm font-bold transition ${
                        checked
                          ? 'border-blue-500 bg-blue-50 text-blue-800'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) =>
                          setNewUser({
                            ...newUser,
                            extraRoles: e.target.checked
                              ? [...newUser.extraRoles, opt.value]
                              : newUser.extraRoles.filter((r) => r !== opt.value),
                          })
                        }
                      />
                      {opt.label}
                    </label>
                  );
                })}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                دسترسی کاربر جمع نقش اصلی و این نقش‌هاست.
              </p>
            </div>
          </div>
          {newUser.role === 'USER' && (
            <div className="flex items-center">
              <input
                type="checkbox"
                id="new-user-is-foreign"
                checked={newUser.isForeign}
                onChange={(e) => setNewUser({...newUser, isForeign: e.target.checked})}
                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500/30"
              />
              <label htmlFor="new-user-is-foreign" className="mr-2 block text-sm font-semibold text-slate-800">
                کاربر خارجی است (ورود با ایمیل)
              </label>
            </div>
          )}
          <div>
            <label className={`${formLabelCls} mb-2`}>
              دوره‌های اختصاصی
            </label>
            <div className="max-h-40 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50/60 p-3 ring-1 ring-slate-100">
              {courses.map((course) => (
                <div key={course.id} className="mb-2 flex items-center last:mb-0">
                  <input
                    type="checkbox"
                    id={`new-course-${course.id}`}
                    checked={newUser.selectedCourses.includes(course.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setNewUser({...newUser, selectedCourses: [...newUser.selectedCourses, course.id]});
                      } else {
                        setNewUser({...newUser, selectedCourses: newUser.selectedCourses.filter(id => id !== course.id)});
                      }
                    }}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500/30"
                  />
                  <label htmlFor={`new-course-${course.id}`} className="mr-2 block cursor-pointer text-sm font-semibold text-slate-800">
                    {course.title}
                  </label>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5">
            <input
              type="checkbox"
              checked={newUser.isActive}
              onChange={(e) => setNewUser({...newUser, isActive: e.target.checked})}
              className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500/30"
            />
            <label className="block text-sm font-bold text-slate-800">
              حساب فعال باشد
            </label>
          </div>
          <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-bold text-slate-800 shadow-sm transition hover:bg-slate-50"
            >
              انصراف
            </button>
            <button
              type="submit"
              className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-black text-white shadow-md shadow-indigo-600/35 ring-2 ring-indigo-500/25 transition hover:bg-indigo-700"
            >
              ایجاد {getAddButtonText()}
            </button>
          </div>
        </form>
      </Modal>

      {}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="ویرایش کاربر"
      >
        {editingUser ? (
          <form onSubmit={handleUpdateUser} className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
              <label className={formLabelCls}>
                نام
              </label>
              <input
                type="text"
                value={editingUser.firstName || ''}
                onChange={(e) => handleEditFirstNameChange(e.target.value)}
                className={`${formInputCls} ${editFirstNameError ? formInputErrorCls : ''}`}
                required
              />
              {editFirstNameError && (
                <p className="text-sm text-red-600 mt-1">{editFirstNameError}</p>
              )}
              </div>
              <div>
              <label className={formLabelCls}>
                نام خانوادگی
              </label>
              <input
                type="text"
                value={editingUser.lastName || ''}
                onChange={(e) => handleEditLastNameChange(e.target.value)}
                className={`${formInputCls} ${editLastNameError ? formInputErrorCls : ''}`}
              />
              {editLastNameError && (
                <p className="text-sm text-red-600 mt-1">{editLastNameError}</p>
              )}
              </div>
            </div>
            <div>
              <label className={formLabelCls}>
                نام کاربری <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editingUser.username || ''}
                onChange={(e) => editingUser && setEditingUser({...editingUser, username: e.target.value})}
                className={formInputCls}
                required
                placeholder="نام کاربری منحصر به فرد"
              />
            </div>
            <div>
              <label className={formLabelCls}>
                شماره تلفن {(editingUser.role !== 'ADMIN' && !editingUser.isForeign && !editingUser.email) && <span className="text-red-500">*</span>}
              </label>
              <input
                type="tel"
                value={editingUser.phone || ''}
                onChange={(e) => editingUser && setEditingUser({...editingUser, phone: e.target.value})}
                className={formInputCls}
                required={editingUser.role !== 'ADMIN' && !editingUser.isForeign && !editingUser.email}
                placeholder={editingUser.isForeign ? 'اختیاری' : '09123456789'}
              />
              {editingUser.isForeign && (
                <p className="text-xs text-gray-500 mt-1">برای کاربر خارجی شماره اختیاری است</p>
              )}
              {(editingUser.email && !editingUser.isForeign) && (
                <p className="text-xs text-gray-500 mt-1">با وجود ایمیل، شماره اختیاری است</p>
              )}
            </div>
            <div>
              <label className={formLabelCls}>
                ایمیل
              </label>
              <input
                type="email"
                value={editingUser.email || ''}
                onChange={(e) => editingUser && setEditingUser({...editingUser, email: e.target.value})}
                className={formInputCls}
              />
            </div>
            <div>
              <label className={formLabelCls}>
                نقش
              </label>
              <select
                value={editingUser.role}
                onChange={(e) => editingUser && setEditingUser({...editingUser, role: e.target.value as 'ADMIN' | 'SALES_MANAGER' | 'SALES_PERSON' | 'MENTOR' | 'STORE' | 'USER'})}
                className={formInputCls}
                disabled={editingUser.role === 'ADMIN'}
              >
                <option value="USER">کاربر</option>
                <option value="SALES_PERSON">فروشنده</option>
              <option value="MENTOR">منتور</option>
              <option value="STORE">فروشگاه</option>
                <option value="SALES_MANAGER">مدیر فروش</option>
                {editingUser.role === 'ADMIN' && <option value="ADMIN">مدیر سایت</option>}
              </select>
              <div className="mt-3">
                <label className={formLabelCls}>نقش‌های افزوده (اختیاری)</label>
                <div className="flex flex-wrap gap-2">
                  {EXTRA_ROLE_OPTIONS.filter((opt) => opt.value !== editingUser.role).map((opt) => {
                    const current = editingUser.extraRoles ?? [];
                    const checked = current.includes(opt.value as any);
                    return (
                      <label
                        key={opt.value}
                        className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm font-bold transition ${
                          checked
                            ? 'border-blue-500 bg-blue-50 text-blue-800'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) =>
                            setEditingUser({
                              ...editingUser,
                              extraRoles: (e.target.checked
                                ? [...current, opt.value]
                                : current.filter((r) => r !== opt.value)) as any,
                            })
                          }
                        />
                        {opt.label}
                      </label>
                    );
                  })}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  دسترسی کاربر جمع نقش اصلی و این نقش‌هاست.
                </p>
              </div>
              {editingUser.role === 'ADMIN' && (
                <p className="text-xs text-gray-500 mt-1">نقش مدیر سایت قابل تغییر نیست</p>
              )}
            </div>
            {editingUser.role === 'USER' && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="edit-user-is-foreign"
                  checked={!!editingUser.isForeign}
                  onChange={(e) => editingUser && setEditingUser({...editingUser, isForeign: e.target.checked})}
                  className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500/30"
                />
                <label htmlFor="edit-user-is-foreign" className="mr-2 block text-sm font-semibold text-slate-800">
                  کاربر خارجی است (ورود با ایمیل)
                </label>
              </div>
            )}
            <div className="rounded-2xl border border-amber-100 bg-amber-50/70 p-4 ring-1 ring-amber-100">
              <h3 className="mb-3 border-b border-amber-200/80 pb-2 text-sm font-black text-slate-900">تغییر رمز عبور</h3>
              <div className="mb-4 rounded-xl border border-amber-200/90 bg-white/80 px-3 py-2">
                <p className="text-xs font-semibold leading-relaxed text-amber-950">
                  رمز ذخیره‌شده هش است و قابل نمایش نیست؛ برای تغییر، فقط رمز جدید را وارد کنید.
                </p>
              </div>
              <div>
                <label className={formLabelCls}>
                  رمز عبور جدید (اختیاری)
                </label>
                <input
                  type="password"
                  value={editingPassword}
                  onChange={(e) => setEditingPassword(e.target.value)}
                  className={formInputCls}
                  placeholder="حداقل ۶ کاراکتر"
                  minLength={6}
                />
              </div>
              <div className="mt-3">
                <label className={formLabelCls}>
                  تأیید رمز عبور جدید
                </label>
                <input
                  type="password"
                  value={editingConfirmPassword}
                  onChange={(e) => setEditingConfirmPassword(e.target.value)}
                  className={formInputCls}
                  placeholder="تأیید رمز عبور"
                  minLength={6}
                />
              </div>
            </div>
            <div>
              <label className={`${formLabelCls} mb-2`}>
                دوره‌های اختصاصی
              </label>
              <div className="max-h-40 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50/60 p-3 ring-1 ring-slate-100">
                {courses.map((course) => (
                  <div key={course.id} className="mb-2 flex items-center last:mb-0">
                    <input
                      type="checkbox"
                      id={`edit-course-${course.id}`}
                      checked={editingUserCourses.includes(course.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setEditingUserCourses([...editingUserCourses, course.id]);
                        } else {
                          setEditingUserCourses(editingUserCourses.filter(id => id !== course.id));
                        }
                      }}
                      className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500/30"
                    />
                    <label htmlFor={`edit-course-${course.id}`} className="mr-2 block cursor-pointer text-sm font-semibold text-slate-800">
                      {course.title}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5">
              <input
                type="checkbox"
                checked={editingUser.isActive}
                onChange={(e) => editingUser && setEditingUser({...editingUser, isActive: e.target.checked})}
                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500/30"
              />
              <label className="block text-sm font-bold text-slate-800">
                حساب فعال باشد
              </label>
            </div>

            {/* Block Status Section */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm ring-1 ring-slate-100">
              <h3 className={`${modalSectionTitleCls} mb-3 border-0 pb-0`}>وضعیت بلاک (Rate Limit)</h3>
              {editingUser.isBlocked ? (
                <div className="mb-2 rounded-xl border border-orange-200 bg-orange-50/90 p-4">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-bold text-orange-900">کاربر بلاک شده است</span>
                    <button
                      type="button"
                      onClick={() => handleUnblockUser(editingUser.id)}
                      className="rounded-xl bg-emerald-600 px-3 py-1.5 text-xs font-black text-white shadow-sm hover:bg-emerald-700"
                    >
                      آنبلاک
                    </button>
                  </div>
                  {editingUser.blockedUntil && (
                    <p className="text-xs text-orange-700">
                      بلاک تا: {new Date(editingUser.blockedUntil).toLocaleString('fa-IR')}
                    </p>
                  )}
                  {editingUser.rateLimitViolations && editingUser.rateLimitViolations > 0 && (
                    <p className="text-xs text-orange-700 mt-1">
                      تعداد تخلفات: {editingUser.rateLimitViolations}
                    </p>
                  )}
                  {editingUser.lastRateLimitViolation && (
                    <p className="text-xs text-orange-700 mt-1">
                      آخرین تخلف: {new Date(editingUser.lastRateLimitViolation).toLocaleString('fa-IR')}
                    </p>
                  )}
                </div>
              ) : (
                <div className="mb-2 rounded-xl border border-emerald-200 bg-emerald-50/90 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-bold text-emerald-900">بلاک نشده</span>
                    <button
                      type="button"
                      onClick={() => handleBlockUser(editingUser.id)}
                      className="rounded-xl bg-orange-600 px-3 py-1.5 text-xs font-black text-white shadow-sm hover:bg-orange-700"
                    >
                      بلاک
                    </button>
                  </div>
                  {editingUser.rateLimitViolations && editingUser.rateLimitViolations > 0 && (
                    <p className="text-xs text-green-700 mt-2">
                      تعداد تخلفات قبلی: {editingUser.rateLimitViolations}
                    </p>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-bold text-slate-800 shadow-sm transition hover:bg-slate-50"
              >
                انصراف
              </button>
              <button
                type="submit"
                className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-black text-white shadow-md shadow-indigo-600/35 ring-2 ring-indigo-500/25 transition hover:bg-indigo-700"
              >
                به‌روزرسانی کاربر
              </button>
            </div>
          </form>
        ) : null}
      </Modal>
    </div>
  );
};

export default UsersManagement;
